import React, { useState, useEffect } from 'react';
import { 
  Upload, AlertCircle, CheckCircle, Clock, Search, FileText, X, Loader, 
  Info, ArrowLeft, Image as ImageIcon, RefreshCw, Building 
} from 'lucide-react';

interface PendingOrder {
  id: number;
  order_number: string;
  customer_name: string;
  total: number;
  transaction_id: string | null;
  status: string;
  order_date: string;
  payment_method?: string;
  items_count?: number;
}

interface BankDetail {
  is_default: any;
  id: number;
  bank_name: string;
  account_number: string;
  account_name: string;
  branch?: string;
  currency?: string;
  swift_code?: string;
  routing_number?: string;
  instructions?: string;
}

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://sparklinggems.shop/api'
  : 'http://localhost/sparkling-gems-backend-php/api';

const PaymentProofUpload: React.FC = () => {
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<PendingOrder | null>(null);
  const [transactionRef, setTransactionRef] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofPreview, setProofPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // Start as true
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Bank details for display
  const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);
  const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);
  const [loadingBanks, setLoadingBanks] = useState(false);

  // FIXED: Get user ID and verify authentication on mount with proper async handling
  useEffect(() => {
    const checkAuthAndLoad = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('Checking authentication...');
        
        // Try to get user from localStorage (multiple possible keys)
        let user = null;
        let userIdValue = null;
        
        // Check sparkingUser first
        const sparklingUserStr = localStorage.getItem('sparklingUser');
        if (sparklingUserStr) {
          user = JSON.parse(sparklingUserStr);
          console.log('User from sparklingUser:', user);
        }
        
        // Fallback to 'user'
        if (!user) {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            user = JSON.parse(userStr);
            console.log('User from user:', user);
          }
        }
        
        if (user && user.id) {
          userIdValue = user.id;
          setUserId(userIdValue);
          setIsAuthenticated(true);
          console.log('User authenticated with ID:', userIdValue);
        } else {
          // Try to get userId from localStorage
          const storedUserId = localStorage.getItem('userId');
          if (storedUserId) {
            userIdValue = parseInt(storedUserId);
            setUserId(userIdValue);
            setIsAuthenticated(true);
            console.log('User ID from localStorage:', userIdValue);
          }
        }
        
        // If still no user, try to get from token
        if (!userIdValue) {
          const token = localStorage.getItem('token');
          if (token) {
            console.log('Token found, fetching user info...');
            try {
              const response = await fetch(`${API_BASE_URL}/auth/me.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await response.json();
              if (data.success && data.user && data.user.id) {
                userIdValue = data.user.id;
                setUserId(userIdValue);
                setIsAuthenticated(true);
                // Save user to localStorage
                localStorage.setItem('sparklingUser', JSON.stringify(data.user));
                console.log('User fetched from token, ID:', userIdValue);
              }
            } catch (err) {
              console.error('Error fetching user from token:', err);
            }
          }
        }
        
        // If authenticated, fetch orders and bank details
        if (userIdValue) {
          console.log('Authenticated, fetching data for user ID:', userIdValue);
          await Promise.all([
            fetchPendingOrders(userIdValue),
            fetchBankDetails()
          ]);
        } else {
          console.log('Not authenticated, showing login message');
          setIsAuthenticated(false);
        }
        
      } catch (err) {
        console.error('Auth error:', err);
        setError('Failed to load user information. Please refresh the page.');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthAndLoad();
  }, []);

  const fetchPendingOrders = async (uid: number) => {
    if (!uid) return;
    
    try {
      console.log('Fetching pending orders for user:', uid);
      const url = `${API_BASE_URL}/orders/get-pending-proof.php?user_id=${uid}`;
      console.log('Fetch URL:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Pending orders response:', data);
      
      if (data.success) {
        setPendingOrders(data.orders || []);
        setDebugInfo(`Found ${data.orders?.length || 0} pending orders`);
      } else {
        setError(data.error || 'Failed to fetch orders');
        setDebugInfo(`Error: ${data.error}`);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Connection error. Please check your internet and try again.');
      setDebugInfo(`Fetch error: ${err}`);
    }
  };

  const fetchBankDetails = async () => {
    setLoadingBanks(true);
    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/update.php`);
      const data = await response.json();
      if (data.success) {
        setBankDetails(data.banks || []);
        // Select default bank if available
        const defaultBank = data.banks?.find((b: BankDetail) => b.is_default) || data.banks?.[0];
        setSelectedBank(defaultBank || null);
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('File selected:', file.name, file.type, file.size);
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPG, PNG)');
        return;
      }
      
      setProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProofPreview(reader.result as string);
        console.log('File preview created');
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedOrder) {
      setError('Please select an order');
      return;
    }
    
    if (!proofFile) {
      setError('Please upload proof of payment');
      return;
    }
    
    if (!transactionRef.trim()) {
      setError('Please enter transaction reference number');
      return;
    }
    
    setUploading(true);
    setError(null);
    setDebugInfo('Converting file to base64...');
    setUploadProgress(30);
    
    try {
      const proofBase64 = await fileToBase64(proofFile);
      console.log('Base64 length:', proofBase64.length);
      
      setUploadProgress(60);
      
      const payload = {
        order_id: selectedOrder.id,
        payment_proof: proofBase64,
        transaction_id: transactionRef,
        user_id: userId
      };
      
      console.log('Sending payload to:', `${API_BASE_URL}/orders/upload-proof.php`);
      setDebugInfo('Sending to server...');
      
      const response = await fetch(`${API_BASE_URL}/orders/upload-proof.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      console.log('Response status:', response.status);
      setUploadProgress(80);
      
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse response:', responseText);
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}`);
      }
      
      console.log('Parsed response:', data);
      setUploadProgress(100);
      
      if (data.success) {
        setSuccess('Payment proof uploaded successfully! Our team will verify your payment within 24 hours.');
        setDebugInfo('Upload successful!');
        setSelectedOrder(null);
        setProofFile(null);
        setProofPreview(null);
        setTransactionRef('');
        setSearchTerm('');
        
        // Refresh the list
        if (userId) {
          await fetchPendingOrders(userId);
        }
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
        setTimeout(() => setDebugInfo(null), 5000);
      } else {
        setError(data.error || 'Failed to upload proof');
        setDebugInfo(`Server error: ${data.error}`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Connection error. Please try again.');
      setDebugInfo(`Upload error: ${err}`);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const clearSelection = () => {
    setSelectedOrder(null);
    setProofFile(null);
    setProofPreview(null);
    setTransactionRef('');
    setError(null);
    setDebugInfo(null);
  };

  const filteredOrders = pendingOrders.filter(order => 
    order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (order.transaction_id && order.transaction_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <Loader size={40} className="animate-spin text-gold-600 mx-auto mb-4" />
              <p className="text-stone-500">Loading your account...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, show login message with back button
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-50 pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-6 transition-colors"
          >
            <ArrowLeft size={14} /> Back
          </button>
          
          <div className="bg-white border border-stone-200 rounded-sm shadow-lg p-12 text-center">
            <AlertCircle size={48} className="mx-auto text-amber-500 mb-4" />
            <h2 className="font-serif text-2xl text-stone-900 mb-2">Authentication Required</h2>
            <p className="text-stone-600 mb-6">Please log in to view your orders and upload payment proof.</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/auth'}
                className="bg-gold-600 text-white px-8 py-3 rounded-sm hover:bg-gold-500 transition-colors"
              >
                Go to Login
              </button>
              <button
                onClick={() => window.location.reload()}
                className="border border-stone-300 text-stone-600 px-8 py-3 rounded-sm hover:bg-stone-50 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        {/* Back button */}
        <button
          onClick={() => window.history.back()}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-6 transition-colors"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <h1 className="font-serif text-3xl text-stone-900 mb-2">Payment Verification</h1>
        <p className="text-stone-600 mb-8">Upload screenshots of bank transfers for manual verification</p>

        {/* Upload Progress Bar */}
        {uploading && (
          <div className="mb-6">
            <div className="flex justify-between mb-1">
              <span className="text-xs text-stone-500">Uploading...</span>
              <span className="text-xs text-stone-500">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-stone-200 rounded-full h-2">
              <div 
                className="bg-gold-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Debug Info - Remove in production */}
        {debugInfo && (
          <div className="mb-6 p-3 bg-stone-100 border border-stone-200 text-stone-600 text-xs rounded-sm font-mono">
            <strong>Debug:</strong> {debugInfo}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-sm flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
              <X size={16} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm rounded-sm flex items-start gap-2">
            <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span className="flex-1">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-sm shadow-lg overflow-hidden">
          {/* Bank Details Summary */}
          {selectedBank && (
            <div className="p-6 border-b border-stone-200 bg-blue-50">
              <div className="flex items-center gap-2 mb-3">
                <Building size={18} className="text-gold-600" />
                <h3 className="font-serif text-lg text-stone-900">Bank Transfer Details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-stone-500">Bank</p>
                  <p className="font-medium">{selectedBank.bank_name}</p>
                  {selectedBank.branch && <p className="text-xs text-stone-500">{selectedBank.branch}</p>}
                </div>
                <div>
                  <p className="text-xs text-stone-500">Account Number</p>
                  <p className="font-mono font-medium">{selectedBank.account_number}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Account Name</p>
                  <p className="font-medium">{selectedBank.account_name}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Currency</p>
                  <p className="font-medium">{selectedBank.currency || 'PHP'}</p>
                </div>
              </div>
              {selectedBank.instructions && (
                <div className="mt-3 p-3 bg-white rounded-sm border border-blue-200 text-xs text-blue-800">
                  {selectedBank.instructions}
                </div>
              )}
            </div>
          )}

          {/* Order Selection */}
          <div className="p-6 border-b border-stone-200">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-xl text-stone-900">Select Order</h2>
              {selectedOrder && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gold-600 hover:text-gold-500 flex items-center gap-1"
                >
                  <X size={14} /> Change Order
                </button>
              )}
            </div>
            
            {pendingOrders.length === 0 ? (
              <div className="text-center py-12 bg-stone-50 rounded-sm">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                <p className="text-stone-600 font-medium">No pending orders found</p>
                <p className="text-sm text-stone-400 mt-2">All your orders have been verified</p>
                <button
                  onClick={() => userId && fetchPendingOrders(userId)}
                  className="mt-4 text-gold-600 hover:text-gold-500 text-sm underline flex items-center gap-1 mx-auto"
                >
                  <RefreshCw size={12} /> Refresh
                </button>
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="relative mb-4">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input
                    type="text"
                    placeholder="Search by order number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-600 focus:outline-none text-sm"
                  />
                </div>

                {/* Orders List */}
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {filteredOrders.length === 0 ? (
                    <p className="text-center py-8 text-stone-400">No orders match your search</p>
                  ) : (
                    filteredOrders.map((order) => (
                      <label
                        key={order.id}
                        className={`flex items-center p-4 border rounded-sm cursor-pointer transition-all ${
                          selectedOrder?.id === order.id
                            ? 'border-gold-600 bg-gold-50 ring-1 ring-gold-600'
                            : 'border-stone-200 hover:border-gold-600 hover:bg-stone-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="order"
                          value={order.id}
                          checked={selectedOrder?.id === order.id}
                          onChange={() => setSelectedOrder(order)}
                          className="sr-only"
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <div>
                            <p className="font-medium text-stone-900">{order.order_number}</p>
                            <p className="text-xs text-stone-500">{new Date(order.order_date).toLocaleDateString()}</p>
                            {order.transaction_id && (
                              <p className="text-xs text-stone-400 mt-1">Ref: {order.transaction_id}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gold-600">₱ {order.total.toLocaleString()}</p>
                            <span className="inline-block px-2 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded-full mt-1">
                              Pending
                            </span>
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Upload Form */}
          {selectedOrder && (
            <form onSubmit={handleSubmit} className="p-6 bg-stone-50">
              <h2 className="font-serif text-xl text-stone-900 mb-4">Upload Proof of Payment</h2>
              
              {/* Order Summary */}
              <div className="mb-6 p-4 bg-white border border-stone-200 rounded-sm">
                <p className="text-xs text-stone-500 mb-1">Selected Order</p>
                <p className="font-medium">{selectedOrder.order_number}</p>
                <p className="text-sm text-stone-600 mt-2">
                  Amount to Pay: <span className="font-bold text-gold-600">₱ {selectedOrder.total.toLocaleString()}</span>
                </p>
              </div>

              {/* Transaction Reference */}
              <div className="mb-6">
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                  Transaction Reference No. <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={transactionRef}
                  onChange={(e) => setTransactionRef(e.target.value)}
                  placeholder="e.g. TR-123456789"
                  className="w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-600 focus:outline-none rounded-sm bg-white"
                  required
                />
                <p className="text-[10px] text-stone-400 mt-1">Enter the reference number from your bank transfer receipt</p>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">
                  Upload Screenshot <span className="text-red-500">*</span>
                </label>
                
                {proofPreview ? (
                  <div className="mb-4">
                    <div className="relative inline-block">
                      <img
                        src={proofPreview}
                        alt="Proof preview"
                        className="h-40 w-auto rounded-sm border border-stone-200 shadow-sm"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setProofFile(null);
                          setProofPreview(null);
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ) : null}

                <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-sm cursor-pointer transition-all border-stone-300 hover:border-gold-600 hover:bg-white bg-white">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 text-stone-400">
                    <ImageIcon className="w-8 h-8 mb-3" />
                    <p className="text-sm font-medium">Click to upload screenshot</p>
                    <p className="text-xs mt-1">JPG, PNG up to 5MB</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={uploading}
                className="w-full bg-gold-600 text-white py-4 uppercase text-xs tracking-[0.2em] hover:bg-gold-500 transition-colors shadow-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed rounded-sm"
              >
                {uploading ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Submitting... {uploadProgress}%
                  </>
                ) : (
                  'Submit Verification'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 p-6 rounded-sm">
          <h3 className="font-serif text-lg text-stone-900 mb-3 flex items-center gap-2">
            <Info size={18} className="text-gold-600" />
            Payment Verification Process
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-stone-600">
            <li>Select the order you've made a bank transfer for</li>
            <li>Enter the transaction reference number from your bank receipt</li>
            <li>Upload a clear screenshot or photo of your bank transfer receipt</li>
            <li>Our concierge team will verify your payment within 24 hours</li>
            <li>You'll receive an email confirmation once your payment is verified</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default PaymentProofUpload;